'use client';

/**
 * Bookings Hook
 * 
 * Provides booking management functionality for customers.
 * Uses the CustomerAuthProvider for API access.
 * 
 * Transforms Route Royal's CustomerBookingResponseDto into the Royale
 * Booking type, computing scheduledAt/duration from startTime/endTime
 * and deriving bookingType from the subject field.
 */

import { useState, useCallback, useEffect } from 'react';
import { useCustomerAuth } from '@/components/auth/CustomerAuthProvider';
import type { Booking, BookingStakeholder, BookingType, CreateBookingRequest, AvailabilitySlot } from '@/types/customer';

// ============================================================================
// Route Royal response shape (what the API actually returns)
// ============================================================================

interface RawRouteRoyalBooking {
  id: string;
  organisationId?: string;
  status: string;
  startTime: string;
  endTime?: string | null;
  subject?: string | null;
  description?: string | null;
  createdAt: string;
  updatedAt: string;
  bookableResource?: {
    id: string;
    name: string;
    assetId?: string;
    assetType?: string;
    type?: string;
  } | null;
  stakeholders?: Array<{
    role: string;
    id: string;
    name: string;
    isCurrentUser?: boolean;
  }>;
  // Legacy fields that may still appear
  scheduledAt?: string;
  duration?: number;
  bookingType?: string;
  propertyId?: string;
  listingId?: string;
  notes?: string;
}

// ============================================================================
// Transform helpers
// ============================================================================

const BOOKING_TYPE_KEYWORDS: Record<string, BookingType> = {
  viewing: 'VIEWING',
  inspection: 'VIEWING',
  valuation: 'VALUATION',
  consultation: 'CONSULTATION',
  meeting: 'MEETING',
};

function deriveBookingType(raw: RawRouteRoyalBooking): BookingType {
  if (raw.bookingType && ['VIEWING', 'VALUATION', 'CONSULTATION', 'MEETING'].includes(raw.bookingType)) {
    return raw.bookingType as BookingType;
  }
  const subjectLower = (raw.subject ?? '').toLowerCase();
  for (const [keyword, type] of Object.entries(BOOKING_TYPE_KEYWORDS)) {
    if (subjectLower.includes(keyword)) return type;
  }
  return 'MEETING';
}

function computeDurationMinutes(startTime: string, endTime?: string | null): number {
  if (!endTime) return 60;
  const diffMs = new Date(endTime).getTime() - new Date(startTime).getTime();
  return Math.max(Math.round(diffMs / 60_000), 1);
}

function normaliseBooking(raw: RawRouteRoyalBooking): Booking {
  const startTime = raw.startTime ?? raw.scheduledAt ?? raw.createdAt;
  const endTime = raw.endTime ?? null;

  const booking: Booking = {
    id: raw.id,
    status: raw.status as Booking['status'],
    startTime,
    endTime,
    scheduledAt: startTime,
    duration: raw.duration ?? computeDurationMinutes(startTime, endTime),
    subject: raw.subject ?? null,
    description: raw.description ?? null,
    bookingType: deriveBookingType(raw),
    stakeholders: (raw.stakeholders ?? []) as BookingStakeholder[],
    bookableResource: raw.bookableResource ?? null,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
  };
  if (raw.organisationId) booking.organisationId = raw.organisationId;
  const resolvedPropertyId = raw.propertyId ?? raw.bookableResource?.assetId;
  if (resolvedPropertyId) booking.propertyId = resolvedPropertyId;
  if (raw.listingId) booking.listingId = raw.listingId;
  const notesValue = raw.notes ?? raw.description;
  if (notesValue != null) booking.notes = notesValue;
  return booking;
}

// ============================================================================
// Types
// ============================================================================

export interface UseBookingsReturn {
  /** All bookings for the current customer */
  bookings: Booking[];
  /** Whether bookings are loading */
  isLoading: boolean;
  /** Error if any */
  error: string | null;
  /** Refresh bookings */
  refresh: () => Promise<void>;
  /** Get availability slots */
  getAvailability: (params: {
    propertyId?: string;
    listingId?: string;
    bookingType: BookingType;
    startDate: string;
    endDate: string;
  }) => Promise<AvailabilitySlot[]>;
  /** Get a specific booking */
  getBooking: (bookingId: string) => Promise<Booking | null>;
  /** Create a new booking */
  createBooking: (data: CreateBookingRequest) => Promise<Booking | null>;
  /** Cancel a booking */
  cancelBooking: (bookingId: string, reason?: string) => Promise<Booking | null>;
  /** Reschedule a booking */
  rescheduleBooking: (bookingId: string, newScheduledAt: string) => Promise<Booking | null>;
}

// ============================================================================
// Hook
// ============================================================================

export function useBookings(): UseBookingsReturn {
  const { getCustomerClient, isAuthenticated } = useCustomerAuth();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const client = getCustomerClient();
    if (!client) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await client.getBookings();
      const rawItems = (response?.items ?? []) as unknown as RawRouteRoyalBooking[];
      setBookings(rawItems.map(normaliseBooking));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load bookings';

      if (errorMessage.includes('404') || 
          errorMessage.includes('not found') || 
          errorMessage.includes('Cannot GET') ||
          errorMessage.includes('API server unavailable') || 
          errorMessage.includes('Failed to fetch') ||
          errorMessage.includes('Network')) {
        setBookings([]);
      } else {
        console.error('[useBookings] Failed to fetch bookings:', err);
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  }, [getCustomerClient]);

  useEffect(() => {
    if (isAuthenticated) {
      refresh();
    } else {
      setBookings([]);
    }
  }, [isAuthenticated, refresh]);

  const getAvailability = useCallback(async (params: {
    propertyId?: string;
    listingId?: string;
    bookingType: BookingType;
    startDate: string;
    endDate: string;
  }): Promise<AvailabilitySlot[]> => {
    const client = getCustomerClient();
    if (!client) return [];

    try {
      return await client.getAvailability(params);
    } catch (err) {
      console.error('[useBookings] Failed to get availability:', err);
      return [];
    }
  }, [getCustomerClient]);

  const getBooking = useCallback(async (bookingId: string): Promise<Booking | null> => {
    const client = getCustomerClient();
    if (!client) return null;

    try {
      const raw = await client.getBooking(bookingId) as unknown as RawRouteRoyalBooking;
      return normaliseBooking(raw);
    } catch (err) {
      console.error('[useBookings] Failed to get booking:', err);
      return null;
    }
  }, [getCustomerClient]);

  const createBooking = useCallback(async (data: CreateBookingRequest): Promise<Booking | null> => {
    const client = getCustomerClient();
    if (!client) return null;

    try {
      const raw = await client.createBooking(data) as unknown as RawRouteRoyalBooking;
      const booking = normaliseBooking(raw);
      setBookings((prev) => [booking, ...prev]);
      return booking;
    } catch (err) {
      console.error('[useBookings] Failed to create booking:', err);
      setError(err instanceof Error ? err.message : 'Failed to create booking');
      return null;
    }
  }, [getCustomerClient]);

  const cancelBooking = useCallback(async (
    bookingId: string,
    reason?: string
  ): Promise<Booking | null> => {
    const client = getCustomerClient();
    if (!client) return null;

    try {
      const raw = await client.cancelBooking(bookingId, reason) as unknown as RawRouteRoyalBooking;
      const booking = normaliseBooking(raw);
      setBookings((prev) => prev.map((b) => (b.id === bookingId ? booking : b)));
      return booking;
    } catch (err) {
      console.error('[useBookings] Failed to cancel booking:', err);
      setError(err instanceof Error ? err.message : 'Failed to cancel booking');
      return null;
    }
  }, [getCustomerClient]);

  const rescheduleBooking = useCallback(async (
    bookingId: string,
    newScheduledAt: string
  ): Promise<Booking | null> => {
    const client = getCustomerClient();
    if (!client) return null;

    try {
      const raw = await client.rescheduleBooking(bookingId, newScheduledAt) as unknown as RawRouteRoyalBooking;
      const booking = normaliseBooking(raw);
      setBookings((prev) => prev.map((b) => (b.id === bookingId ? booking : b)));
      return booking;
    } catch (err) {
      console.error('[useBookings] Failed to reschedule booking:', err);
      setError(err instanceof Error ? err.message : 'Failed to reschedule booking');
      return null;
    }
  }, [getCustomerClient]);

  return {
    bookings,
    isLoading,
    error,
    refresh,
    getAvailability,
    getBooking,
    createBooking,
    cancelBooking,
    rescheduleBooking,
  };
}

export default useBookings;
